import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Typography,
	Box,
	Card,
	CardContent,
	CardMedia,
	Grid,
	Button,
	Divider,
	Tabs,
	Tab,
	IconButton,
	useTheme,
} from '@mui/material';
import {
	Facebook,
	LinkedIn,
	Twitter,
	Star,
	StarBorder,
	StarHalf,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../redux/reducers/cartSlice';
import {
	fetchDrugById,
	fetchAllDrugs,
	fetchDrugCategories,
} from '../../redux/middleware/medication/drugs/drugs';
import { APP_URLS } from '../../../urls';
import bannerImage from '../../data/about/m-treat-doctor.jpg';
import { shades } from '../../../theme';

// Placeholder for social sharing URLs
const shareUrls = {
	facebook: '#',
	linkedin: '#',
	twitter: '#',
};

const Item = () => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Redux state
	const drug = useSelector((state) => state.drugs.drug);
	const [reviews, setReviews] = useState([]);
	const [loadingReviews, setLoadingReviews] = useState(false);
	const [reviewError, setReviewError] = useState(null);
	const allDrugs = useSelector((state) => state.drugs.allDrugs);
	const loading = useSelector((state) => state.drugs.loading);
	const error = useSelector((state) => state.drugs.error);
	const categories = useSelector((state) => state.drugs.categories); // Add categories from Redux

	// Local state
	const [selectedTab, setSelectedTab] = useState(0);
	const [quantity, setQuantity] = useState(1);
	const [categoryMap, setCategoryMap] = useState({});
	const [convertedPrice, setConvertedPrice] = useState(null);
	const [isConverting, setIsConverting] = useState(false);
	const [conversionError, setConversionError] = useState(null);
	const rating = drug?.average_rating || 0;
	const totalRatings = drug?.total_ratings || 0;

	// Add conversion function
	const convertCurrency = async (price, currency) => {
		if (!price) return;
		setIsConverting(true);
		try {
			const response = await fetch('/medication/drug/convert_currency/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					price: parseFloat(price),
					currency,
				}),
			});
			const data = await response.json();
			if (response.ok) {
				setConvertedPrice({
					price: data.converted_price,
					currency: data.user_currency,
				});
			} else {
				setConversionError(data.error);
			}
		} catch (error) {
			setConversionError('Failed to convert price');
		} finally {
			setIsConverting(false);
		}
	};

	// Add useEffect
	useEffect(() => {
		if (drug?.price && drug?.currency) {
			convertCurrency(drug.price, drug.currency);
		}
	}, [drug]);

	// Fetch Drug Categories & Map ID -> Name
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const fetchedCategories = await dispatch(
					fetchDrugCategories(),
				).unwrap();
				const mapping = {};
				fetchedCategories.forEach((cat) => {
					mapping[cat.id] = cat.name; // Map category ID to name
				});
				setCategoryMap(mapping);
			} catch (err) {
				console.error('Error fetching categories:', err);
			}
		};
		fetchCategories();
	}, [dispatch]);

	// Fetch Drug Details
	useEffect(() => {
		if (id) {
			dispatch(fetchDrugById(id));
		}
	}, [id, dispatch]);

	// Update your fetchReviews function in the useEffect
	useEffect(() => {
		const fetchReviews = async () => {
			if (selectedTab === 2 && drug?.id) {
				try {
					setLoadingReviews(true);
					setReviewError(null);

					// Try the base endpoint instead
					const response = await fetch(
						'http://127.0.0.1:8000/medication/reviews/',
					);

					if (!response.ok) {
						throw new Error(`Failed to fetch reviews: ${response.status}`);
					}

					const data = await response.json();
					console.log('All reviews:', data);

					// Filter reviews for this drug
					const drugReviews = data.filter(
						(review) =>
							review.drug === drug.id || review.drug === parseInt(drug.id),
					);

					console.log(
						`Found ${drugReviews.length} reviews for drug ${drug.id}`,
					);
					setReviews(drugReviews);
				} catch (error) {
					console.error('Error fetching reviews:', error);
					setReviewError(error.message);
				} finally {
					setLoadingReviews(false);
				}
			}
		};

		fetchReviews();
	}, [selectedTab, drug?.id]);

	// Fetch All Drugs
	useEffect(() => {
		dispatch(fetchAllDrugs());
	}, [dispatch]);

	// Add to Cart
	const handleAddToCart = (drug) => {
		dispatch(
			addToCart({
				item: {
					id: drug.id,
					name: drug.name,
					price: drug.price,
					currency: drug.currency,
					image: drug.image,
					count: quantity, // Include selected quantity
				},
				message: `${quantity} ${drug.name}(s) added to cart`,
			}),
		);
	};

	// Handle Tabs
	const handleTabChange = (event, newValue) => {
		setSelectedTab(newValue);
	};

	// Handle Quantity Change
	const handleQuantityChange = (type) => {
		setQuantity((prev) =>
			type === 'increase' ? prev + 1 : Math.max(1, prev - 1),
		);
	};

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error: {error}</Typography>;

	// Ensure data is available
	const drugName = drug?.name || 'Unknown Drug';
	const drugCategory =
		drug?.categories && drug.categories.length > 0
			? drug.categories[0].name
			: 'No Category Found';
	const drugPrice = drug?.price ? `${drug.currency} ${drug.price}` : 'N/A';
	const drugImage = drug?.img || '/placeholder-image.jpg';
	const drugDescription = drug?.description || 'No description available';
	const drugPurpose = drug?.purpose || 'No purpose specified';

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				backgroundColor: isDarkMode ? shades.secondary[800] : shades.white[100],
				color: isDarkMode ? shades.white[100] : shades.secondary[500],
			}}
		>
			<Box sx={{ width: { xs: '95%', sm: '95%', md: '80%' }, paddingTop: 3 }}>
				{/* Drug Details Panel */}
				<Grid container spacing={4} sx={{ marginTop: 2 }}>
					{/* Left Column: Image Section */}
					<Grid item xs={12} sm={6} md={6}>
						<Card
							sx={{
								height: '100%',
								width: '100%',
								background: isDarkMode
									? shades.secondary[800]
									: shades.white[100],
								color: isDarkMode ? shades.white[100] : shades.secondary[500],
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								maxWidth: { xs: '300px', sm: '260px', md: '500px' },
								border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
							}}
						>
							<CardMedia
								component="img"
								image={drugImage}
								alt={drugName}
								sx={{
									width: '100%',
									height: { xs: '200px', sm: '300px', md: '400px' },
									objectFit: 'contain',
									backgroundColor: isDarkMode
										? shades.secondary[700]
										: '#f5f5f5',
									borderRadius: '8px',
									margin: '0 auto',
									display: 'block',
									padding: '16px',
									boxShadow: isDarkMode
										? '0 2px 4px rgba(0,0,0,0.3)'
										: '0 2px 4px rgba(0,0,0,0.1)',
								}}
							/>

							{/* Thumbnail Section */}
							<Box
								sx={{
									display: 'flex',
									gap: 1,
									mt: 2,
									px: 2,
									width: '100%',
									overflowX: 'auto',
								}}
							>
								{[1, 2, 3, 4].map((index) => (
									<Card
										key={index}
										sx={{
											minWidth: '60px',
											height: '60px',
											cursor: 'pointer',
											backgroundColor: isDarkMode
												? shades.secondary[700]
												: shades.white[100],
											border: '2px solid transparent',
											'&:hover': {
												border: `2px solid ${isDarkMode ? shades.accent[300] : '#1976d2'}`,
											},
										}}
									>
										<CardMedia
											component="img"
											image={drugImage}
											alt={`${drugName} view ${index}`}
											sx={{
												height: '100%',
												width: '100%',
												objectFit: 'contain',
												p: 0.5,
											}}
										/>
									</Card>
								))}
							</Box>
						</Card>
					</Grid>

					{/* Right Column: Details Section */}
					<Grid item xs={12} sm={6} md={6}>
						<Typography
							variant="h3"
							gutterBottom
							fontWeight="bold"
							sx={{
								color: isDarkMode ? shades.white[100] : shades.secondary[500],
							}}
						>
							{drugName}
						</Typography>
						<Typography
							variant="subtitle1"
							sx={{
								color: isDarkMode ? shades.neutral[300] : shades.secondary[400],
							}}
						>
							{drugPurpose}
						</Typography>
						<Typography
							variant="h4"
							gutterBottom
							sx={{
								color: isDarkMode ? shades.white[100] : shades.secondary[500],
							}}
						>
							<span style={{ fontWeight: 'bold' }}>Category: </span>
							{drugCategory}
						</Typography>

						<Typography
							variant="h5"
							sx={{
								marginY: 2,
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.3rem' },
								color: isDarkMode ? shades.accent[300] : shades.accent[700],
							}}
						>
							{isConverting
								? 'Converting...'
								: conversionError
									? `${drug?.currency || 'USD'} ${drug?.price?.toLocaleString() || 'N/A'}`
									: convertedPrice?.currency
										? `${convertedPrice.currency} ${convertedPrice.price.toLocaleString()}`
										: `${drug?.currency || 'USD'} ${drug?.price?.toLocaleString() || 'N/A'}`}
						</Typography>

						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								{[...Array(5)].map((_, index) => {
									const value = index + 1;
									return (
										<Star
											key={index}
											aria-label={`${value} stars`}
											sx={{
												color:
													value <= rating
														? '#FFB921'
														: value - 0.5 <= rating
															? '#FFB921'
															: isDarkMode
																? 'rgba(255,255,255,0.3)'
																: '#D3D3D3',
												fontSize: '20px',
											}}
										/>
									);
								})}
							</Box>
							<Typography
								variant="body2"
								sx={{ color: isDarkMode ? shades.neutral[300] : '#666', ml: 1 }}
								aria-label={`${rating} out of 5 stars, ${totalRatings} ratings`}
							>
								{rating.toFixed(1)} ({totalRatings} ratings)
							</Typography>
						</Box>

						{/* Quantity Selector */}
						<Box
							display="flex"
							alignItems="center"
							sx={{
								marginBottom: 2,
								color: isDarkMode ? shades.white[100] : shades.secondary[500],
							}}
						>
							Quantity:
							<Button
								variant="outlined"
								size="small"
								onClick={() => handleQuantityChange('decrease')}
								disabled={quantity <= 1}
								sx={{
									borderColor: isDarkMode ? shades.neutral[400] : undefined,
									color: isDarkMode ? shades.white[100] : undefined,
									'&:hover': {
										borderColor: isDarkMode ? shades.white[100] : undefined,
										backgroundColor: isDarkMode
											? 'rgba(255,255,255,0.05)'
											: undefined,
									},
									'&.Mui-disabled': {
										borderColor: isDarkMode
											? 'rgba(255,255,255,0.1)'
											: undefined,
										color: isDarkMode ? 'rgba(255,255,255,0.3)' : undefined,
									},
								}}
							>
								-
							</Button>
							<Typography
								variant="h6"
								sx={{
									marginX: 2,
									color: isDarkMode ? shades.white[100] : 'inherit',
								}}
							>
								{quantity}
							</Typography>
							<Button
								variant="outlined"
								size="small"
								onClick={() => handleQuantityChange('increase')}
								sx={{
									borderColor: isDarkMode ? shades.neutral[400] : undefined,
									color: isDarkMode ? shades.white[100] : undefined,
									'&:hover': {
										borderColor: isDarkMode ? shades.white[100] : undefined,
										backgroundColor: isDarkMode
											? 'rgba(255,255,255,0.05)'
											: undefined,
									},
								}}
							>
								+
							</Button>
						</Box>

						{/* Add to Cart Button */}
						<Button
							variant="contained"
							onClick={() => handleAddToCart(drug)}
							sx={{
								backgroundColor: '#2783f7',
								fontSize: { xs: '0.4rem', sm: '0.6rem', md: '1rem' },
								color: 'white',
								'&:hover': {
									backgroundColor: '#1a6ccc',
									transform: 'translateY(-2px)',
									boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
								},
								transition: 'transform 0.2s, box-shadow 0.2s',
							}}
						>
							Add to Cart
						</Button>

						{/* Social Sharing */}
						<Box
							display="flex"
							alignItems="center"
							mt={3}
							sx={{
								color: isDarkMode ? shades.white[100] : 'inherit',
							}}
						>
							<Typography variant="body1" mr={2}>
								Share:
							</Typography>

							<IconButton
								sx={{
									color: isDarkMode ? shades.white[100] : 'black',
									'&:hover': {
										color: 'white',
										backgroundColor: '#2783f7',
									},
								}}
								component="a"
								href={shareUrls.facebook}
							>
								<Facebook />
							</IconButton>

							<IconButton
								sx={{
									color: isDarkMode ? shades.white[100] : 'black',
									'&:hover': {
										color: 'white',
										backgroundColor: '#2783f7',
									},
								}}
								component="a"
								href={shareUrls.linkedin}
							>
								<LinkedIn />
							</IconButton>

							<IconButton
								sx={{
									color: isDarkMode ? shades.white[100] : 'black',
									'&:hover': {
										color: 'white',
										backgroundColor: '#2783f7',
									},
								}}
								component="a"
								href={shareUrls.twitter}
							>
								<Twitter />
							</IconButton>
						</Box>
					</Grid>
				</Grid>

				{/* Tabs Section */}
				<Box sx={{ marginTop: 4 }}>
					<Tabs
						value={selectedTab}
						onChange={handleTabChange}
						sx={{
							width: '100%',
							overflow: 'hidden',
							'& .MuiTab-root': {
								color: isDarkMode ? shades.white[100] : 'black',
								fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
								padding: { xs: '6px 12px', sm: '8px 16px', md: '10px 20px' },
							},
							'& .MuiTabs-indicator': {
								backgroundColor: isDarkMode ? shades.accent[300] : 'black',
							},
							'& .MuiTabs-flexContainer': {
								justifyContent: 'space-between',
							},
						}}
					>
						<Tab label="Description" />
						<Tab label="Product Details" />
						<Tab label="Reviews" />
					</Tabs>

					<Divider
						sx={{
							borderColor: isDarkMode
								? 'rgba(255,255,255,0.1)'
								: 'rgba(0,0,0,0.1)',
						}}
					/>

					<CardContent
						sx={{
							backgroundColor: isDarkMode
								? shades.secondary[800]
								: shades.white[100],
						}}
					>
						{/* Description Tab */}
						{selectedTab === 0 && (
							<Typography
								variant="body1"
								sx={{
									fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
									color: isDarkMode ? shades.white[100] : 'inherit',
								}}
							>
								{drugDescription}
							</Typography>
						)}

						{/* Product Details Tab */}
						{selectedTab === 1 && (
							<Box>
								{/* Each detail section with dark mode styling */}
								{[
									{
										label: 'Treating Diseases',
										value: drug?.treating_diseases,
									},
									{ label: 'How It Works', value: drug?.how_it_works },
									{ label: 'Dosage Form', value: drug?.dosage_form },
									{ label: 'Side Effects', value: drug?.side_effects },
									{ label: 'Important Info', value: drug?.important_info },
									{
										label: 'Special Precautions',
										value: drug?.special_precautions,
									},
									{ label: 'Stock', value: drug?.stock },
									{ label: 'Expiration Date', value: drug?.expiration_date },
								].map((item, index) => (
									<Box key={index} display="flex" flexDirection="column" mb={2}>
										<Typography
											fontWeight="bold"
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
												color: isDarkMode ? shades.accent[300] : 'inherit',
											}}
										>
											{item.label}
										</Typography>
										<Typography
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											{item.value}
										</Typography>
									</Box>
								))}
							</Box>
						)}

						{/* Reviews Tab */}
						{selectedTab === 2 && (
							<Box>
								{loadingReviews ? (
									<Typography
										sx={{ color: isDarkMode ? shades.white[100] : 'inherit' }}
									>
										Loading reviews...
									</Typography>
								) : reviewError ? (
									<Typography color="error">
										Error loading reviews: {reviewError}
									</Typography>
								) : reviews?.length > 0 ? (
									reviews.map((review, index) => (
										<Box key={index} sx={{ marginBottom: 2 }}>
											<Typography
												variant="body2"
												sx={{
													fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
													color: isDarkMode ? shades.accent[300] : '#2783f7',
												}}
											>
												{renderStars(review.rating)} / 5
											</Typography>
											<Typography
												sx={{
													fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
													color: isDarkMode ? shades.white[100] : 'inherit',
												}}
											>
												{review.comment}
											</Typography>
										</Box>
									))
								) : (
									<Typography
										sx={{ color: isDarkMode ? shades.white[100] : 'inherit' }}
									>
										No reviews available for this drug.
									</Typography>
								)}
							</Box>
						)}
					</CardContent>
				</Box>

				{/* Recommendation Section */}
				<Box sx={{ marginTop: 2, marginBottom: 4 }}>
					<Typography
						variant="h5"
						gutterBottom
						sx={{
							color: isDarkMode ? shades.white[100] : 'inherit',
							fontWeight: 600,
						}}
					>
						You may also like
					</Typography>
					<Grid container spacing={2}>
						{allDrugs?.slice(0, 4)?.map((recommendedDrug) => (
							<Grid item xs={12} sm={6} md={3} key={recommendedDrug.id}>
								<Card
									sx={{
										backgroundColor: isDarkMode
											? shades.secondary[800]
											: shades.white[100],
										border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
										transition: 'transform 0.2s, box-shadow 0.2s',
										'&:hover': {
											transform: 'translateY(-4px)',
											boxShadow: isDarkMode
												? '0 4px 12px rgba(0,0,0,0.3)'
												: '0 4px 12px rgba(0,0,0,0.1)',
										},
									}}
								>
									<CardMedia
										component="img"
										image={recommendedDrug.img || '/placeholder-image.jpg'}
										alt={recommendedDrug.name}
										sx={{ height: 200 }}
									/>
									<CardContent>
										<Typography
											variant="h6"
											gutterBottom
											sx={{
												color: isDarkMode
													? shades.white[100]
													: shades.secondary[500],
												fontWeight: 600,
											}}
										>
											{recommendedDrug.name}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: isDarkMode
													? shades.accent[300]
													: shades.accent[700],
												fontWeight: 600,
											}}
										>
											{recommendedDrug.price}
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				</Box>
			</Box>
		</Box>
	);
};

export default Item;
